import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PageTransition from '../components/PageTransition';
import { motion, AnimatePresence } from 'framer-motion';
import Toast, { ToastType } from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SuccessAnimation from '../components/SuccessAnimation';
import LocationSelectionModal from '../components/LocationSelectionModal';
import { getCountries, getCities, getDistricts, getCarriers, createOrder, linkCartItemToOrder } from '../services/sparePartsService';
import { formatPhoneNumber, cleanPhoneNumber, isValidTurkishMobile, handlePhoneKeyDown } from '../utils/phoneUtils';
import { notifyAccountCacheUpdate } from '../utils/accountCacheUtils';
import { useMenu } from '../contexts/MenuContext';

interface LocationState {
  userAddress: any;
  cartItems: any[];
  totalAmount: number;
}

interface Country {
  Id: number;
  Ad: string;
}

interface City {
  Id: number;
  Ad: string;
}

interface District {
  Id: number;
  Ad: string;
}

interface Carrier {
  Id: number;
  Ad: string;
}

export default function OrderAddress() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userAddress, cartItems, totalAmount } = location.state as LocationState || {};
  const { isMenuButtonVisible } = useMenu();

  // Form states
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [landlinePhone, setLandlinePhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  // Data states
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);

  // Loading states
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(false);

  // Modal states
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isDistrictModalOpen, setIsDistrictModalOpen] = useState(false);
  const [isCarrierModalOpen, setIsCarrierModalOpen] = useState(false);

  // Search states
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');
  const [carrierSearchTerm, setCarrierSearchTerm] = useState('');

  // Error states
  const [addressError, setAddressError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [districtError, setDistrictError] = useState(false);
  const [carrierError, setCarrierError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' as ToastType });

  // Order processing state
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Initialization tracking
  const isInitializing = useRef(false);

  // Toast functions - memoized
  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToast({ isVisible: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Redirect if no data
  useEffect(() => {
    if (!userAddress || !cartItems) {
      navigate('/cart');
    }
  }, [userAddress, cartItems, navigate]);

  // Initialize form with user data
  useEffect(() => {
    if (userAddress && userAddress.data && userAddress.data.length > 0) {
      const userData = userAddress.data[0];
      setAddress(userData.Adres || '');
      setPostalCode(userData.PostaKodu || '');
      setLandlinePhone(formatPhoneNumber(userData.Telefon1 || ''));
      
      // Ülke, İl, İlçe adlarını geçici olarak göster (sunucudan veri gelmeden)
      if (userData.UlkeAdi || userData.IlAdi || userData.IlceAdi) {
        // Geçici placeholder objeler oluştur
        if (userData.UlkeAdi) {
          setSelectedCountry({ Id: 0, Ad: userData.UlkeAdi });
        }
        if (userData.IlAdi) {
          setSelectedCity({ Id: 0, Ad: userData.IlAdi });
        }
        if (userData.IlceAdi) {
          setSelectedDistrict({ Id: 0, Ad: userData.IlceAdi });
        }
      }
    }
  }, [userAddress]);

  // Initialize data and set defaults - optimized single effect
  useEffect(() => {
    let isMounted = true;
    isInitializing.current = true;

    const initializeData = async () => {
      try {
        // Load countries and carriers in parallel
        const [countriesResponse, carriersResponse] = await Promise.all([
          getCountries(),
          getCarriers()
        ]);

        if (!isMounted) return;

        const countryList = countriesResponse.data || [];
        const carrierList = carriersResponse.data || [];
        
        setCountries(countryList);
        setCarriers(carrierList);

        // Set default country if we have user data
        if (userAddress && userAddress.data && userAddress.data.length > 0 && countryList.length > 0) {
      const userData = userAddress.data[0];
          let defaultCountry = null;
      
      if (userData.Ulke) {
            defaultCountry = countryList.find((c: Country) => c.Id === userData.Ulke);
        }
          
          if (!defaultCountry) {
        // Default to Türkiye
            defaultCountry = countryList.find((c: Country) => c.Id === 1);
          }

          if (defaultCountry) {
            setSelectedCountry(defaultCountry);
            
            // Load cities for the default country
            try {
              const citiesResponse = await getCities(defaultCountry.Id);
              if (!isMounted) return;

              const cityList = citiesResponse.data || [];
              setCities(cityList);

              // Set default city if available
              if (userData.Il && cityList.length > 0) {
                const defaultCity = cityList.find((c: City) => c.Id === userData.Il);
                if (defaultCity) {
                  setSelectedCity(defaultCity);

                  // Load districts for the default city
                  try {
                    const districtsResponse = await getDistricts(defaultCity.Id);
                    if (!isMounted) return;

                    const districtList = districtsResponse.data || [];
                    setDistricts(districtList);

                    // Set default district if available
                    if (userData.Ilce && districtList.length > 0) {
                      const defaultDistrict = districtList.find((d: District) => d.Id === userData.Ilce);
                      if (defaultDistrict) {
                        setSelectedDistrict(defaultDistrict);
                      }
                    }
                  } catch (error) {
                    console.error('İlçe listesi yüklenirken hata:', error);
                    showToast('İlçe listesi yüklenirken bir hata oluştu.', 'error');
                  }
                }
              }
            } catch (error) {
              console.error('İl listesi yüklenirken hata:', error);
              showToast('İl listesi yüklenirken bir hata oluştu.', 'error');
            }
          }
        }
      } catch (error) {
        console.error('Başlangıç verileri yüklenirken hata:', error);
        showToast('Veri yükleme sırasında bir hata oluştu.', 'error');
      } finally {
        // Initialization completed
        isInitializing.current = false;
      }
    };

    initializeData();

    return () => {
      isMounted = false;
      isInitializing.current = false;
    };
  }, [userAddress, showToast]);

  // Handle manual country selection changes
  useEffect(() => {
    // Skip if this is during initialization
    if (isInitializing.current) return;
    
    if (selectedCountry) {
      // User manually selected a country - fetch cities
      fetchCities(selectedCountry.Id);
      // Reset dependent selections
      setSelectedCity(null);
      setSelectedDistrict(null);
      setDistricts([]);
    } else {
      // User cleared country selection
      setCities([]);
      setSelectedCity(null);
      setDistricts([]);
      setSelectedDistrict(null);
    }
  }, [selectedCountry]);

  // Handle manual city selection changes  
  useEffect(() => {
    // Skip if this is during initialization
    if (isInitializing.current) return;
    
    if (selectedCity) {
      // User manually selected a city - fetch districts
      fetchDistricts(selectedCity.Id);
      // Reset dependent selections
      setSelectedDistrict(null);
    } else {
      // User cleared city selection
      setDistricts([]);
      setSelectedDistrict(null);
    }
  }, [selectedCity]);

  const fetchCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const data = await getCountries();
      setCountries(data.data || []);
    } catch (error) {
      console.error('Ülke listesi yüklenirken hata:', error);
      showToast('Ülke listesi yüklenirken bir hata oluştu.', 'error');
    } finally {
      setLoadingCountries(false);
    }
  }, [showToast]);

  const fetchCities = useCallback(async (countryId: number) => {
    setLoadingCities(true);
    try {
      const data = await getCities(countryId);
      setCities(data.data || []);
    } catch (error) {
      console.error('İl listesi yüklenirken hata:', error);
      showToast('İl listesi yüklenirken bir hata oluştu.', 'error');
    } finally {
      setLoadingCities(false);
    }
  }, [showToast]);

  const fetchDistricts = useCallback(async (cityId: number) => {
    setLoadingDistricts(true);
    try {
      const data = await getDistricts(cityId);
      setDistricts(data.data || []);
    } catch (error) {
      console.error('İlçe listesi yüklenirken hata:', error);
      showToast('İlçe listesi yüklenirken bir hata oluştu.', 'error');
    } finally {
      setLoadingDistricts(false);
    }
  }, [showToast]);

  const fetchCarriers = useCallback(async () => {
    setLoadingCarriers(true);
    try {
      const data = await getCarriers();
      setCarriers(data.data || []);
    } catch (error) {
      console.error('Taşıyıcı listesi yüklenirken hata:', error);
      showToast('Taşıyıcı listesi yüklenirken bir hata oluştu.', 'error');
    } finally {
      setLoadingCarriers(false);
    }
  }, [showToast]);

  // Form alanlarının validasyonu - optimized with real-time updates
  const validateForm = useCallback((forceUpdate = false) => {
    // Mevcut error durumlarını hesapla
    const newAddressError = !address.trim();
    
    // Posta kodu kontrolü (zorunlu değil ama girilmişse geçerli olmalı)
    let newPostalCodeError = false;
    if (postalCode.trim()) {
      const code = postalCode.trim();
      if (!/^\d{5}$/.test(code) || parseInt(code) < 1000 || parseInt(code) > 99999) {
        newPostalCodeError = true;
      }
    }
    
    const newCountryError = !selectedCountry;
    const newCityError = !selectedCity;
    const newDistrictError = !selectedDistrict;
    
    // Telefon numarası kontrolü - phoneError state'i manuel olarak yönetildiği için
    // burada sadece mevcut state'i koruyalım
    const newPhoneError = phoneError;
    
    const newCarrierError = false; // Carrier zorunlu değil

    // Force update ise veya form submit edilmişse error state'leri güncelle
    if (forceUpdate || formSubmitted) {
      // Sadece değişen error state'leri güncelle
      if (addressError !== newAddressError) {
        setAddressError(newAddressError);
      }
      if (postalCodeError !== newPostalCodeError) {
        setPostalCodeError(newPostalCodeError);
      }
      if (countryError !== newCountryError) {
        setCountryError(newCountryError);
      }
      if (cityError !== newCityError) {
        setCityError(newCityError);
      }
      if (districtError !== newDistrictError) {
        setDistrictError(newDistrictError);
      }
      if (phoneError !== newPhoneError) {
        setPhoneError(newPhoneError);
      }
      if (carrierError !== newCarrierError) {
        setCarrierError(newCarrierError);
      }
    }

    // Validation sonucu - telefon kontrolü handleSubmit'te yapılıyor
    return !(newAddressError || newPostalCodeError || newCountryError || newCityError || newDistrictError);
  }, [
    address, postalCode, selectedCountry, selectedCity, selectedDistrict, landlinePhone, formSubmitted,
    addressError, postalCodeError, countryError, cityError, districtError, phoneError, carrierError
  ]);

  const handleCountrySelect = useCallback((country: Country | null) => {
    setSelectedCountry(country);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setIsCountryModalOpen(false);
    // Real-time validation (if form was submitted)
    if (formSubmitted) {
      setTimeout(() => validateForm(), 0);
    }
  }, [formSubmitted, validateForm]);

  const handleCitySelect = useCallback((city: City | null) => {
    setSelectedCity(city);
    setSelectedDistrict(null);
    setIsCityModalOpen(false);
    // Real-time validation (if form was submitted)
    if (formSubmitted) {
      setTimeout(() => validateForm(), 0);
    }
  }, [formSubmitted, validateForm]);

  const handleDistrictSelect = useCallback((district: District | null) => {
    setSelectedDistrict(district);
    setIsDistrictModalOpen(false);
    // Real-time validation (if form was submitted)
    if (formSubmitted) {
      setTimeout(() => validateForm(), 0);
    }
  }, [formSubmitted, validateForm]);

  const handleCarrierSelect = useCallback((carrier: Carrier | null) => {
    setSelectedCarrier(carrier);
    setIsCarrierModalOpen(false);
    // Carrier zorunlu olmadığı için error temizleme gerekmez
  }, []);

  // Handle input changes - optimized 
  const handleAddressChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setAddress(value);
    // Real-time validation (if form was submitted)
    if (formSubmitted) {
      setTimeout(() => validateForm(), 0);
    }
  }, [formSubmitted, validateForm]);

  // Posta kodu için özel handler (sadece sayı)
  const handlePostalCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Sadece sayıları al
    if (value.length <= 5) { // 5 haneli limit
      setPostalCode(value);
      // Real-time validation (if form was submitted)
      if (formSubmitted) {
        setTimeout(() => validateForm(), 0);
      }
    }
  }, [formSubmitted, validateForm]);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setLandlinePhone(formatted);
    
    // Telefon alanına yazılmaya başlandığında error durumunu temizle
    if (formSubmitted && phoneError && formatted.trim()) {
      setPhoneError(false);
    }
    
    // Geçerli bir numara girildiğinde validateForm çağırmaya gerek yok
    // Çünkü zaten yukarıda phoneError'ı false yaptık
  }, [formSubmitted, phoneError, validateForm]);

      // Telefon alanında backspace işlemini yönet - phoneUtils'den kullanıyoruz
  const phoneKeyDownHandler = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    handlePhoneKeyDown(e, setLandlinePhone, formSubmitted, phoneError, setPhoneError);
  }, [formSubmitted, phoneError]);

  // Search change handlers - memoized
  const handleCountrySearchChange = useCallback((value: string) => {
    setCountrySearchTerm(value);
  }, []);

  const handleCitySearchChange = useCallback((value: string) => {
    setCitySearchTerm(value);
  }, []);

  const handleDistrictSearchChange = useCallback((value: string) => {
    setDistrictSearchTerm(value);
  }, []);

  const handleCarrierSearchChange = useCallback((value: string) => {
    setCarrierSearchTerm(value);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form submit edildiğini işaretle
    setFormSubmitted(true);
    
    // Telefon numarası kontrolü - manuel olarak yapalım
    const isEmpty = !landlinePhone.trim();
    const isInvalid = landlinePhone.trim() && !isValidTurkishMobile(landlinePhone);
    const phoneHasError = isEmpty || !!isInvalid; // Boolean'a dönüştür
    
    // Telefon error state'ini güncelle
    if (phoneError !== phoneHasError) {
      setPhoneError(phoneHasError);
    }
    
    // Force update ile validation yap (error state'leri güncelle)
    const isValid = validateForm(true);
    
    // Telefon hatası varsa veya diğer alanlar geçersizse
    if (phoneHasError || !isValid) {
      showToast('Lütfen tüm zorunlu alanları doldurun.', 'error');
      return;
    }

    // Onay dialogunu aç
    setIsConfirmationOpen(true);
  };

  if (!userAddress || !cartItems) {
    return null;
  }

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Teslimat Bilgileri
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Siparişinizin teslimat adresini belirleyin
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Adres */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Adres <span className="text-red-500">*</span>
              </label>
              <textarea
                value={address}
                onChange={handleAddressChange}
                rows={3}
                className={formSubmitted && addressError ? 'form-textarea-error w-full' : 'form-textarea w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                placeholder="Adres bilgilerinizi girin..."
              />
            </div>

                        {/* Ülke, İl, İlçe - Responsive Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Ülke Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ülke <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  onClick={() => setIsCountryModalOpen(true)}
                    tabIndex={0}
                    className={`${formSubmitted && countryError ? 'form-combobox-error' : 'form-combobox'} w-full cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500`}
                >
                  <span className={selectedCountry ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                    {selectedCountry ? selectedCountry.Ad : 'Ülke seçin'}
                  </span>
                  <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCountryModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* İl Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                İl <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  onClick={() => selectedCountry && setIsCityModalOpen(true)}
                    tabIndex={selectedCountry ? 0 : -1}
                    className={`${formSubmitted && cityError ? 'form-combobox-error' : 'form-combobox'} w-full ${
                    selectedCountry 
                      ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' 
                      : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                  }`}
                >
                  <span className={selectedCity ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                    {selectedCity ? selectedCity.Ad : 'İl seçin'}
                  </span>
                  <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCityModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* İlçe Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                İlçe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  onClick={() => selectedCity && setIsDistrictModalOpen(true)}
                    tabIndex={selectedCity ? 0 : -1}
                    className={`${formSubmitted && districtError ? 'form-combobox-error' : 'form-combobox'} w-full ${
                    selectedCity 
                      ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' 
                      : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                  }`}
                >
                  <span className={selectedDistrict ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                    {selectedDistrict ? selectedDistrict.Ad : 'İlçe seçin'}
                  </span>
                  <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isDistrictModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              </div>
            </div>

            {/* Telefon, Posta Kodu, Taşıyıcı - Responsive Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Telefon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telefon <span className="text-red-500">*</span>
              </label>
              <input
                  id="phone-input"
                type="tel"
                value={landlinePhone}
                onChange={handlePhoneChange}
                  onKeyDown={phoneKeyDownHandler}
                  maxLength={17}
                  className={formSubmitted && phoneError ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                placeholder="Telefon numaranızı girin..."
              />
              </div>

              {/* Posta Kodu */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Posta Kodu
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={postalCode}
                  onChange={handlePostalCodeChange}
                  maxLength={5}
                  className={formSubmitted && postalCodeError ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                  placeholder="Posta kodu (isteğe bağlı)"
                />
            </div>

            {/* Taşıyıcı Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Taşıyıcı
              </label>
              <div className="relative">
                <div
                  onClick={() => setIsCarrierModalOpen(true)}
                    tabIndex={0}
                    className={`${formSubmitted && carrierError ? 'form-combobox-error' : 'form-combobox'} w-full cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500`}
                >
                  <span className={selectedCarrier ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                    {selectedCarrier ? selectedCarrier.Ad : 'Taşıyıcı seçin'}
                  </span>
                  <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCarrierModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              </div>
            </div>

            {/* Sipariş Notları */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sipariş Notları
              </label>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                rows={3}
                className="form-textarea w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500"
                placeholder="Sipariş ile ilgili notlarınız..."
              />
            </div>

            {/* Sipariş Özeti */}
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-emerald-900/25 dark:to-green-800/30 border border-green-200/50 dark:border-green-700/50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Sipariş Özeti
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cartItems?.length} ürün
                  </p>
                </div>
              </div>
              <div className="flex justify-center sm:justify-between items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-lg font-medium text-gray-900 dark:text-white">
                  <span className="sm:hidden">Toplam:</span>
                  <span className="hidden sm:inline">Toplam Tutar:</span>
                </div>
                <div className="text-xl font-bold text-green-600 dark:text-green-400 ml-4 sm:ml-0">
                  ₺{totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="group flex-1 flex items-center justify-center gap-2 font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95"
              >
                Sepete Geri Dön
              </button>
              
              <button
                type="submit"
                disabled={isProcessingOrder}
                className={`group flex-1 font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 text-white flex items-center justify-center gap-2 ${
                  isProcessingOrder 
                    ? 'bg-gray-400 cursor-not-allowed border border-gray-300' 
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500 shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 transform hover:scale-[1.02] active:scale-[0.98] border border-green-500 hover:border-green-600 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700'
                }`}
              >
                {isProcessingOrder ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sipariş Oluşturuluyor...
                  </>
                ) : (
                  <>
                    Siparişi Tamamla
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Ülke Seçim Modal'ı */}
        <LocationSelectionModal
          isOpen={isCountryModalOpen}
          onClose={() => setIsCountryModalOpen(false)}
          onSelect={handleCountrySelect}
          items={countries}
          selectedId={selectedCountry?.Id}
          loading={loadingCountries}
          title="Ülke Seçin"
          searchPlaceholder="Ülke ara..."
          emptyMessage="Uygun ülke bulunamadı."
          searchTerm={countrySearchTerm}
          isRequired={true}
          onSearchChange={handleCountrySearchChange}
        />

        {/* İl Seçim Modal'ı */}
        <LocationSelectionModal
          isOpen={isCityModalOpen}
          onClose={() => setIsCityModalOpen(false)}
          onSelect={handleCitySelect}
          items={cities}
          selectedId={selectedCity?.Id}
          loading={loadingCities}
          title="İl Seçin"
          searchPlaceholder="İl ara..."
          emptyMessage={selectedCountry ? 'Uygun il bulunamadı.' : 'Önce ülke seçin.'}
          searchTerm={citySearchTerm}
          isRequired={true}
          onSearchChange={handleCitySearchChange}
        />

        {/* İlçe Seçim Modal'ı */}
        <LocationSelectionModal
          isOpen={isDistrictModalOpen}
          onClose={() => setIsDistrictModalOpen(false)}
          onSelect={handleDistrictSelect}
          items={districts}
          selectedId={selectedDistrict?.Id}
          loading={loadingDistricts}
          title="İlçe Seçin"
          searchPlaceholder="İlçe ara..."
          emptyMessage={selectedCity ? 'Uygun ilçe bulunamadı.' : 'Önce il seçin.'}
          searchTerm={districtSearchTerm}
          isRequired={true}
          onSearchChange={handleDistrictSearchChange}
        />

        {/* Taşıyıcı Seçim Modal'ı */}
        <LocationSelectionModal
          isOpen={isCarrierModalOpen}
          onClose={() => setIsCarrierModalOpen(false)}
          onSelect={handleCarrierSelect}
          items={carriers}
          selectedId={selectedCarrier?.Id}
          loading={loadingCarriers}
          title="Taşıyıcı Seçin"
          searchPlaceholder="Taşıyıcı ara..."
          emptyMessage="Uygun taşıyıcı bulunamadı."
          searchTerm={carrierSearchTerm}
          onSearchChange={handleCarrierSearchChange}
        />

        {/* Sipariş Onay Diyaloğu */}
        <ConfirmationDialog
          isOpen={isConfirmationOpen}
          title="Sipariş Onayı"
          message="Sipariş bilgileriniz kontrol edilip onaylandıktan sonra işleme alınacaktır. Girdiğiniz bilgilerin doğruluğundan eminseniz Tamam düğmesine tıklayın."
          autoClose={true}
          onCancel={() => setIsConfirmationOpen(false)}
          onConfirm={async () => {
            setIsProcessingOrder(true);
            
            try {
              // 1. Sipariş oluştur
              const orderData = {
                address: address.trim(),
                postalCode: postalCode.trim(),
                country: selectedCountry,
                city: selectedCity,
                district: selectedDistrict,
                phone: landlinePhone.trim(),
                orderNotes: orderNotes.trim(),
                carrier: selectedCarrier || { Id: 0 }
              };

              const orderResponse = await createOrder(orderData);
              const orderId = orderResponse.data[0].Id;
              const orderNo = orderResponse.data[0].No;

              // 2. Sepetteki her öğeyi siparişe bağla
              for (const cartItem of cartItems) {
                await linkCartItemToOrder(cartItem, orderId);
              }

              // 3. OrderId'yi sakla ve başarı animasyonunu göster
              setCreatedOrderId(orderId);
              setIsSuccessVisible(true);
              
              // 4. React Query cache'ini güncelle
              queryClient.invalidateQueries({ queryKey: ['orders'] });
              queryClient.invalidateQueries({ queryKey: ['cart'] });
              
              // 5. Account cache'i güncelle
              notifyAccountCacheUpdate('order');
              
            } catch (error) {
              console.error('Sipariş oluşturma hatası:', error);
              showToast('Sipariş oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.', 'error');
            } finally {
              setIsProcessingOrder(false);
            }
          }}
        />

        {/* Başarı Animasyonu */}
        <SuccessAnimation
          isVisible={isSuccessVisible}
          message="Siparişiniz başarıyla oluşturuldu!<br />Sipariş detaylarınız yükleniyor..."
          duration={3000}
          onAnimationComplete={() => {
            // Önce başarı animasyonunu kapat
            setIsSuccessVisible(false);
            
            // Animasyon tamamen kapandıktan sonra kısa bir süre bekle
            setTimeout(() => {
              // Sipariş detay sayfasına yönlendir
              if (createdOrderId) {
                navigate(`/order/${createdOrderId}`);
              } else {
                navigate('/'); // Fallback olarak ana sayfaya yönlendir
              }
            }, 500);
          }}
        />

        {/* Toast */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      </div>
    </PageTransition>
  );
} 